// client/src/pages/wholesaler/CatalogDiscounts.js
// Catalog-Wide Discount Management — FULLY TRANSLATED
// 3 types: Flat, Tiered, Targeted (brand/category)
// Tier limits with upgrade nudge banner

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  Plus, Trash2, Tag, Layers, Target, Edit2, ToggleLeft, ToggleRight,
  AlertTriangle, ArrowUpCircle, X, Check, Info
} from 'lucide-react';
import Navbar from '../../components/common/Navbar';
import Footer from '../../components/common/Footer';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import axios from 'axios';
import { API_URL, getAuthHeaders, catalogDiscountsAPI } from '../../services/api';
import useRangeSelect from '../../hooks/useRangeSelect';
import DragSelectOverlay from '../../components/common/DragSelectOverlay';
import FloatingActionBar from '../../components/common/FloatingActionBar';
import toast from 'react-hot-toast';
import ConfirmModal from '../../components/common/ConfirmModal';

// ================================================================
// UPGRADE NUDGE BANNER
// ================================================================
const UpgradeNudgeBanner = ({ current, limit, tier, t }) => {
  const navigate = useNavigate();
  const isAtLimit = current >= limit;
  const isNear = current >= Math.floor(limit * 0.8);
  if (limit === -1 || !isNear) return null;
  
  return (
    <div className={`rounded-lg p-4 mb-6 flex items-center justify-between flex-wrap gap-3 ${
      isAtLimit ? 'bg-red-50 border-2 border-red-200' : 'bg-amber-50 border border-amber-200'
    }`}>
      <div className="flex items-center gap-3">
        {isAtLimit ? <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0" /> : <Info className="w-5 h-5 text-amber-500 flex-shrink-0" />}
        <div>
          <p className={`text-sm font-semibold ${isAtLimit ? 'text-red-800' : 'text-amber-800'}`}>
            {isAtLimit 
              ? t('catalogDiscounts.upgrade.limitReached', { current, limit })
              : t('catalogDiscounts.upgrade.limitNear', { current, limit })
            }
          </p>
          <p className={`text-xs ${isAtLimit ? 'text-red-600' : 'text-amber-600'}`}>
            {isAtLimit ? t('catalogDiscounts.upgrade.limitReachedDesc') : t('catalogDiscounts.upgrade.limitNearDesc')}
          </p>
        </div>
      </div>
      <button onClick={() => navigate('/wholesaler/subscription')}
        className="px-4 py-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg text-sm font-medium hover:from-purple-700 hover:to-blue-700 flex items-center gap-2 flex-shrink-0">
        <ArrowUpCircle className="w-4 h-4" /> {t('catalogDiscounts.upgrade.upgradeButton')}
      </button>
    </div>
  );
};

// ================================================================
// CREATE/EDIT MODAL
// ================================================================
const DiscountModal = ({ discount, onClose, onSave, brands, categories, t }) => {
  const [form, setForm] = useState(() => {
    if (discount) return {
      type: discount.type, name: discount.name,
      percentage: discount.percentage || '', tiers: discount.tiers || [{ minAmount: 0, maxAmount: '', percentage: 0 }],
      targetType: discount.targetType || 'brand', targetValue: discount.targetValue || '', targetCategory: '',
      targetPercentage: discount.targetPercentage || '', stackable: discount.stackable || false,
      expiresAt: discount.expiresAt ? new Date(discount.expiresAt).toISOString().split('T')[0] : ''
    };
    return {
      type: 'flat', name: '', percentage: '',
      tiers: [{ minAmount: 0, maxAmount: '', percentage: 0 }, { minAmount: 100, maxAmount: '', percentage: 3 }],
      targetType: 'brand', targetValue: '', targetCategory: '', targetPercentage: '', stackable: false, expiresAt: ''
    };
  });
  const [subcategories, setSubcategories] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const set = (key, val) => setForm(p => ({ ...p, [key]: val }));
  const addTier = () => {
    const last = form.tiers[form.tiers.length - 1];
    set('tiers', [...form.tiers, { minAmount: last.maxAmount || last.minAmount + 100, maxAmount: '', percentage: 0 }]);
  };
  const removeTier = (i) => { if (form.tiers.length > 1) set('tiers', form.tiers.filter((_, idx) => idx !== i)); };
  const updateTier = (i, field, val) => {
    const u = [...form.tiers]; u[i] = { ...u[i], [field]: val === '' ? '' : Number(val) }; set('tiers', u);
  };

  // Fetch subcategories when category is selected for subcategory targeting
  useEffect(() => {
    if (form.targetType === 'subcategory' && form.targetCategory) {
      axios.get(`${API_URL}/catalog-discounts/subcategories?category=${encodeURIComponent(form.targetCategory)}`, { headers: getAuthHeaders() })
        .then(res => setSubcategories(res.data.subcategories || []))
        .catch(() => setSubcategories([]));
    } else {
      setSubcategories([]);
    }
  }, [form.targetType, form.targetCategory]);

  const handleSubmit = async () => {
    setError('');
    if (!form.name.trim()) { setError(t('catalogDiscounts.errors.nameRequired')); return; }
    if (form.type === 'flat' && (!form.percentage || form.percentage <= 0 || form.percentage > 100)) { setError(t('catalogDiscounts.errors.invalidPercentage')); return; }
    if (form.type === 'tiered' && form.tiers.length === 0) { setError(t('catalogDiscounts.errors.addOneTier')); return; }
    if (form.type === 'targeted' && (!form.targetValue || !form.targetPercentage)) { setError(t('catalogDiscounts.errors.selectTargetAndPercentage')); return; }
    setSaving(true);
    try {
      const payload = { type: form.type, name: form.name.trim(), stackable: form.stackable, expiresAt: form.expiresAt || null };
      if (form.type === 'flat') payload.percentage = Number(form.percentage);
      if (form.type === 'tiered') payload.tiers = form.tiers.map(ti => ({ minAmount: Number(ti.minAmount) || 0, maxAmount: ti.maxAmount === '' || ti.maxAmount === null ? null : Number(ti.maxAmount), percentage: Number(ti.percentage) || 0 }));
      if (form.type === 'targeted') { payload.targetType = form.targetType; payload.targetValue = form.targetValue; payload.targetPercentage = Number(form.targetPercentage); }
      await onSave(payload, discount?._id);
      onClose();
    } catch (err) { setError(err.response?.data?.message || t('catalogDiscounts.errors.saveFailed')); }
    finally { setSaving(false); }
  };

  const ic = "w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none";
  const lc = "block text-xs font-medium text-gray-500 mb-1";
  const typeIcons = { flat: Tag, tiered: Layers, targeted: Target };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between rounded-t-xl z-10">
          <h2 className="text-lg font-bold text-gray-900">{discount ? t('catalogDiscounts.modal.editTitle') : t('catalogDiscounts.modal.createTitle')}</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-full"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-5">
          {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded-lg text-sm">{error}</div>}

          {!discount && (
            <div>
              <label className={lc}>{t('catalogDiscounts.modal.discountType')}</label>
              <div className="grid grid-cols-3 gap-2">
                {['flat', 'tiered', 'targeted'].map(tid => {
                  const Icon = typeIcons[tid];
                  const active = form.type === tid;
                  return (
                    <button key={tid} type="button" onClick={() => set('type', tid)}
                      className={`p-3 rounded-lg border-2 text-left transition-all ${active ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`}>
                      <Icon className={`w-5 h-5 mb-1 ${active ? 'text-blue-600' : 'text-gray-400'}`} />
                      <p className={`text-sm font-semibold ${active ? 'text-blue-700' : 'text-gray-700'}`}>{t(`catalogDiscounts.types.${tid}`)}</p>
                      <p className="text-[10px] text-gray-400">{t(`catalogDiscounts.typeDescriptions.${tid}`)}</p>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div>
            <label className={lc}>{t('catalogDiscounts.modal.discountName')}</label>
            <input type="text" value={form.name} onChange={e => set('name', e.target.value)} placeholder={t('catalogDiscounts.modal.namePlaceholder')} className={ic} maxLength={100} />
          </div>

          {form.type === 'flat' && (
            <div>
              <label className={lc}>{t('catalogDiscounts.modal.percentageOff')}</label>
              <input type="number" value={form.percentage} onChange={e => set('percentage', e.target.value)} min="0.1" max="100" step="0.1" placeholder="5" className={ic} />
            </div>
          )}

          {form.type === 'tiered' && (
            <div>
              <label className={lc}>{t('catalogDiscounts.modal.orderValueTiers')}</label>
              <div className="space-y-2">
                {form.tiers.map((tier, i) => (
                  <div key={i} className="flex items-center gap-2 bg-gray-50 p-2 rounded-lg">
                    <div className="flex-1 grid grid-cols-3 gap-2">
                      <div><span className="text-[10px] text-gray-400">{t('catalogDiscounts.modal.fromAmount')}</span><input type="number" value={tier.minAmount} onChange={e => updateTier(i, 'minAmount', e.target.value)} className="w-full px-2 py-1 border rounded text-sm" min="0" /></div>
                      <div><span className="text-[10px] text-gray-400">{t('catalogDiscounts.modal.toAmount')}</span><input type="number" value={tier.maxAmount} onChange={e => updateTier(i, 'maxAmount', e.target.value)} className="w-full px-2 py-1 border rounded text-sm" min="0" placeholder="∞" /></div>
                      <div><span className="text-[10px] text-gray-400">{t('catalogDiscounts.modal.discountPercent')}</span><input type="number" value={tier.percentage} onChange={e => updateTier(i, 'percentage', e.target.value)} className="w-full px-2 py-1 border rounded text-sm" min="0" max="100" /></div>
                    </div>
                    {form.tiers.length > 1 && <button onClick={() => removeTier(i)} className="p-1 text-red-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>}
                  </div>
                ))}
                <button onClick={addTier} className="text-blue-600 hover:text-blue-700 text-sm flex items-center gap-1"><Plus className="w-4 h-4" /> {t('catalogDiscounts.modal.addTier')}</button>
              </div>
            </div>
          )}

          {form.type === 'targeted' && (
            <div className="space-y-3">
              <div>
                <label className={lc}>{t('catalogDiscounts.modal.target')}</label>
                <div className="flex gap-2">
                  {['brand', 'category', 'subcategory'].map(tt => (
                    <button key={tt} type="button" onClick={() => { set('targetType', tt); set('targetValue', ''); set('targetCategory', ''); }}
                      className={`flex-1 py-2 rounded-lg border text-sm font-medium transition-all ${form.targetType === tt ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 text-gray-600'}`}>
                      {t(`catalogDiscounts.modal.${tt}`)}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                {form.targetType === 'brand' ? (
                  <><label className={lc}>{t('catalogDiscounts.modal.brandName')}</label>
                  <div><input type="text" value={form.targetValue} onChange={e => set('targetValue', e.target.value)} placeholder={t('catalogDiscounts.modal.brandPlaceholder')} className={ic} list="brand-suggestions" /><datalist id="brand-suggestions">{brands.map(b => <option key={b} value={b} />)}</datalist></div></>
                ) : form.targetType === 'category' ? (
                  <><label className={lc}>{t('catalogDiscounts.modal.category')}</label>
                  <select value={form.targetValue} onChange={e => set('targetValue', e.target.value)} className={ic}>
                    <option value="">{t('catalogDiscounts.modal.selectCategory')}</option>
                    {categories.map(c => <option key={c} value={c}>{t(`products:categories.${c}`, c?.replace(/_/g, ' '))}</option>)}
                  </select></>
                ) : (
                  <><label className={lc}>{t('catalogDiscounts.modal.category')}</label>
                  <select value={form.targetCategory} onChange={e => { set('targetCategory', e.target.value); set('targetValue', ''); }} className={`${ic} mb-2`}>
                    <option value="">{t('catalogDiscounts.modal.selectCategoryFirst', 'Select a category first')}</option>
                    {categories.map(c => <option key={c} value={c}>{t(`products:categories.${c}`, c?.replace(/_/g, ' '))}</option>)}
                  </select>
                  <label className={lc}>{t('catalogDiscounts.modal.subcategory', 'Subcategory')}</label>
                  <select value={form.targetValue} onChange={e => set('targetValue', e.target.value)} className={ic} disabled={!form.targetCategory}>
                    <option value="">{t('catalogDiscounts.modal.selectSubcategory', 'Select subcategory...')}</option>
                    {subcategories.map(sc => <option key={sc} value={sc}>{t(`products:subcategories.${sc}`, sc?.replace(/_/g, ' '))}</option>)}
                  </select></>
                )}
              </div>
              <div>
                <label className={lc}>{t('catalogDiscounts.modal.discountPercentage')}</label>
                <input type="number" value={form.targetPercentage} onChange={e => set('targetPercentage', e.target.value)} min="0.1" max="100" step="0.1" placeholder="5" className={ic} />
              </div>
            </div>
          )}

          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div><p className="text-sm font-medium text-gray-700">{t('catalogDiscounts.modal.stackable')}</p><p className="text-xs text-gray-400">{t('catalogDiscounts.modal.stackableDesc')}</p></div>
            <button type="button" onClick={() => set('stackable', !form.stackable)}
              className={`relative w-12 h-6 rounded-full transition-colors ${form.stackable ? 'bg-blue-500' : 'bg-gray-300'}`}>
              <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${form.stackable ? 'left-6' : 'left-0.5'}`} />
            </button>
          </div>

          <div><label className={lc}>{t('catalogDiscounts.modal.expiresOptional')}</label><input type="date" value={form.expiresAt} onChange={e => set('expiresAt', e.target.value)} className={ic} /></div>
        </div>
        <div className="sticky bottom-0 bg-white border-t px-6 py-4 flex gap-3 rounded-b-xl">
          <button onClick={handleSubmit} disabled={saving}
            className="flex-1 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium text-sm disabled:opacity-50 flex items-center justify-center gap-2">
            {saving ? t('catalogDiscounts.modal.saving') : <><Check className="w-4 h-4" /> {discount ? t('catalogDiscounts.modal.update') : t('catalogDiscounts.modal.create')}</>}
          </button>
          <button onClick={onClose} className="px-4 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 text-sm">{t('catalogDiscounts.modal.cancel')}</button>
        </div>
      </div>
    </div>
  );
};

// ================================================================
// DISCOUNT CARD
// ================================================================
const DiscountCard = ({ discount, onToggle, onEdit, onDelete, t, isSelected, isDragSelected, onSelect }) => {
  const typeColors = {
    flat: { bg: 'bg-blue-50', border: 'border-blue-200', icon: 'text-blue-500', badge: 'bg-blue-100 text-blue-700' },
    tiered: { bg: 'bg-purple-50', border: 'border-purple-200', icon: 'text-purple-500', badge: 'bg-purple-100 text-purple-700' },
    targeted: { bg: 'bg-emerald-50', border: 'border-emerald-200', icon: 'text-emerald-500', badge: 'bg-emerald-100 text-emerald-700' },
  };
  const c = typeColors[discount.type] || typeColors.flat;
  const Icon = discount.type === 'flat' ? Tag : discount.type === 'tiered' ? Layers : Target;
  const isExpired = discount.expiresAt && new Date(discount.expiresAt) < new Date();

  return (
    <div data-select-id={discount._id} className={`rounded-lg border p-4 transition-all ${isSelected ? 'bg-indigo-50 border-indigo-300' : isDragSelected ? 'bg-blue-50 border-blue-300' : !discount.active || isExpired ? 'bg-gray-50 border-gray-200 opacity-60' : `${c.bg} ${c.border}`}`}>
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={isSelected}
            readOnly onClick={(e) => { e.stopPropagation(); onSelect(discount._id, e); }}
            className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
          />
          <Icon className={`w-5 h-5 ${discount.active && !isExpired ? c.icon : 'text-gray-400'}`} />
          <h3 className="text-sm font-semibold text-gray-900">{discount.name}</h3>
        </div>
        <div className="flex items-center gap-1">
          {discount.stackable && <span className="px-1.5 py-0.5 bg-yellow-100 text-yellow-700 text-[10px] font-medium rounded">{t('catalogDiscounts.labels.stacks')}</span>}
          <span className={`px-2 py-0.5 text-[10px] font-medium rounded ${c.badge}`}>
            {t(`catalogDiscounts.labels.${discount.type}Badge`)}
          </span>
        </div>
      </div>
      <div className="mb-3">
        {discount.type === 'flat' && <p className="text-2xl font-bold text-gray-900">{discount.percentage}% <span className="text-sm font-normal text-gray-500">{t('catalogDiscounts.labels.offEverything')}</span></p>}
        {discount.type === 'tiered' && (
          <div className="space-y-1">{(discount.tiers || []).map((ti, i) => (
            <div key={i} className="flex items-center gap-2 text-xs"><span className="text-gray-500">€{ti.minAmount}{ti.maxAmount ? ` – €${ti.maxAmount}` : '+'}</span><span className="font-bold text-gray-900">{ti.percentage}%</span></div>
          ))}</div>
        )}
        {discount.type === 'targeted' && <p className="text-sm"><span className="font-bold text-gray-900">{discount.targetPercentage}%</span> <span className="text-gray-500">{t('catalogDiscounts.labels.off')}</span> <span className="font-medium text-gray-700 capitalize">{discount.targetType === 'category' ? t('catalogDiscounts.modal.category') : discount.targetType === 'subcategory' ? t('catalogDiscounts.modal.subcategory', 'Subcategory') : discount.targetType}: {discount.targetType === 'category' ? t(`products:categories.${discount.targetValue}`, discount.targetValue) : discount.targetType === 'subcategory' ? t(`products:subcategories.${discount.targetValue}`, discount.targetValue?.replace(/_/g, ' ')) : discount.targetValue}</span></p>}
      </div>
      <div className="flex items-center justify-between text-xs text-gray-400">
        <div className="flex items-center gap-3">
          {discount.expiresAt && <span className={isExpired ? 'text-red-500' : ''}>{isExpired ? t('catalogDiscounts.labels.expired') : `${t('catalogDiscounts.labels.expires')} ${new Date(discount.expiresAt).toLocaleDateString()}`}</span>}
          <span>{t('catalogDiscounts.labels.used')} {discount.timesApplied || 0}x</span>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={() => onToggle(discount._id)} className="p-1 hover:bg-white/50 rounded">{discount.active ? <ToggleRight className="w-5 h-5 text-green-500" /> : <ToggleLeft className="w-5 h-5 text-gray-400" />}</button>
          <button onClick={() => onEdit(discount)} className="p-1 hover:bg-white/50 rounded"><Edit2 className="w-4 h-4 text-gray-400" /></button>
          <button onClick={() => onDelete(discount._id)} className="p-1 hover:bg-white/50 rounded"><Trash2 className="w-4 h-4 text-red-400" /></button>
        </div>
      </div>
    </div>
  );
};

// ================================================================
// MAIN PAGE
// ================================================================
const CatalogDiscounts = () => {
  const { t } = useTranslation(['wholesaler', 'products']);
  const navigate = useNavigate();
  const [discounts, setDiscounts] = useState([]);
  const [usage, setUsage] = useState({ current: 0, limit: 5, isUnlimited: false, canCreate: true, tier: 'starter' });
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editDiscount, setEditDiscount] = useState(null);
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filter, setFilter] = useState('all');
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, title: '', message: '', onConfirm: null });
  const [bulkActionLoading, setBulkActionLoading] = useState(false);

  const fetchDiscounts = useCallback(async () => {
    try {
      const params = filter === 'all' ? {} : { active: filter === 'active' ? 'true' : 'false' };
      const res = await axios.get(`${API_URL}/catalog-discounts`, { params, headers: getAuthHeaders() });
      setDiscounts(res.data.discounts || []); setUsage(res.data.usage || {});
    } catch (err) { console.error(err); } finally { setLoading(false); }
  }, [filter]);

  const fetchMeta = useCallback(async () => {
    try {
      const [br, ca] = await Promise.all([
        axios.get(`${API_URL}/catalog-discounts/brands/wholesaler`, { headers: getAuthHeaders() }),
        axios.get(`${API_URL}/catalog-discounts/categories`, { headers: getAuthHeaders() })
      ]);
      setBrands(br.data.brands || []); setCategories(ca.data.categories || []);
    } catch (err) { console.error(err); }
  }, []);

  useEffect(() => { fetchDiscounts(); }, [fetchDiscounts]);
  useEffect(() => { fetchMeta(); }, [fetchMeta]);

  const handleSave = async (payload, id) => {
    if (id) await axios.put(`${API_URL}/catalog-discounts/${id}`, payload, { headers: getAuthHeaders() });
    else await axios.post(`${API_URL}/catalog-discounts`, payload, { headers: getAuthHeaders() });
    fetchDiscounts();
  };
  const handleToggle = async (id) => {
    try { await axios.patch(`${API_URL}/catalog-discounts/${id}/toggle`, {}, { headers: getAuthHeaders() }); fetchDiscounts(); }
    catch (err) { if (err.response?.data?.upgradeRequired) toast.error(err.response.data.message); }
  };
  const handleDelete = async (id) => {
    setConfirmModal({
      isOpen: true,
      title: t('catalogDiscounts.confirmDeleteTitle', 'Delete Discount'),
      message: t('catalogDiscounts.confirmDelete'),
      onConfirm: async () => {
        setConfirmModal(prev => ({ ...prev, isOpen: false }));
        await axios.delete(`${API_URL}/catalog-discounts/${id}`, { headers: getAuthHeaders() });
        fetchDiscounts();
      }
    });
  };
  const handleEdit = (d) => { setEditDiscount(d); setShowModal(true); };

  const {
    selectedIds, handleItemClick, selectAll, clearSelection,
    dragProps, isDragging, dragRect, dragSelectedIds,
  } = useRangeSelect(discounts);

  const handleBulkDelete = () => {
    setConfirmModal({
      isOpen: true,
      title: t('catalogDiscounts.bulkDeleteTitle', 'Delete Selected'),
      message: t('catalogDiscounts.bulkDeleteMessage', { count: selectedIds.size }) || `Delete ${selectedIds.size} selected discounts?`,
      onConfirm: async () => {
        setConfirmModal(prev => ({ ...prev, isOpen: false }));
        setBulkActionLoading(true);
        try {
          await catalogDiscountsAPI.bulkDelete([...selectedIds]);
          clearSelection();
          fetchDiscounts();
        } catch (err) {
          console.error('Bulk delete failed:', err);
        } finally {
          setBulkActionLoading(false);
        }
      }
    });
  };

  const handleBulkToggle = async () => {
    setBulkActionLoading(true);
    try {
      const selectedDiscounts = discounts.filter(d => selectedIds.has(d._id));
      const majorityActive = selectedDiscounts.filter(d => d.active).length > selectedDiscounts.length / 2;
      const active = !majorityActive;
      await catalogDiscountsAPI.bulkToggle([...selectedIds], active);
      clearSelection();
      fetchDiscounts();
    } catch (err) {
      console.error('Bulk toggle failed:', err);
    } finally {
      setBulkActionLoading(false);
    }
  };

  if (loading) return <div className="min-h-screen bg-gray-50"><Navbar /><div className="flex justify-center items-center h-64"><LoadingSpinner /></div><Footer /></div>;

  const activeDiscounts = discounts.filter(d => d.active);
  const inactiveDiscounts = discounts.filter(d => !d.active);

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="pt-16 lg:pt-0">
        <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{t('catalogDiscounts.pageTitle')}</h1>
              <p className="text-sm text-gray-500 mt-1">{t('catalogDiscounts.pageSubtitle')}</p>
            </div>
            <button onClick={() => { setEditDiscount(null); setShowModal(true); }} disabled={!usage.canCreate}
              className="px-4 py-2.5 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg font-medium text-sm hover:from-blue-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 shadow-md">
              <Plus className="w-4 h-4" /> {t('catalogDiscounts.newDiscount')}
            </button>
          </div>

          {!usage.isUnlimited && <UpgradeNudgeBanner current={usage.current} limit={usage.limit} tier={usage.tier} t={t} />}

          <div className="bg-white rounded-lg border p-4 mb-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">{t('catalogDiscounts.discountUsage')}</span>
              <span className="text-sm text-gray-500">{usage.isUnlimited ? `${usage.current} ${t('catalogDiscounts.active')} (${t('catalogDiscounts.unlimited')})` : `${usage.current} / ${usage.limit}`}</span>
            </div>
            {!usage.isUnlimited && (
              <div className="w-full bg-gray-100 rounded-full h-2">
                <div className={`h-2 rounded-full transition-all ${usage.current >= usage.limit ? 'bg-red-500' : usage.current >= usage.limit * 0.8 ? 'bg-amber-500' : 'bg-blue-500'}`} style={{ width: `${Math.min(100, (usage.current / usage.limit) * 100)}%` }} />
              </div>
            )}
          </div>

          <div className="flex gap-2 mb-6">
            {['all', 'active', 'inactive'].map(f => (
              <button key={f} onClick={() => setFilter(f)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${filter === f ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 border hover:bg-gray-50'}`}>
                {t(`catalogDiscounts.filter.${f}`)}
                <span className="ml-1.5 text-xs opacity-75">({f === 'all' ? discounts.length : f === 'active' ? activeDiscounts.length : inactiveDiscounts.length})</span>
              </button>
            ))}
          </div>

          {discounts.length === 0 ? (
            <div className="bg-white rounded-lg border p-12 text-center">
              <Tag className="w-12 h-12 text-gray-200 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-700 mb-2">{t('catalogDiscounts.noDiscountsYet')}</h3>
              <p className="text-sm text-gray-400 mb-6">{t('catalogDiscounts.noDiscountsDesc')}</p>
              <button onClick={() => { setEditDiscount(null); setShowModal(true); }} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700">
                <Plus className="w-4 h-4 inline mr-1" /> {t('catalogDiscounts.createFirst')}
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" {...dragProps} style={{ position: 'relative' }}>
              <DragSelectOverlay isDragging={isDragging} dragRect={dragRect} />
              {discounts.map(d => <DiscountCard key={d._id} discount={d} onToggle={handleToggle} onEdit={handleEdit} onDelete={handleDelete} t={t} isSelected={selectedIds.has(d._id)} isDragSelected={dragSelectedIds.has(d._id)} onSelect={handleItemClick} />)}
            </div>
          )}

          <div className="mt-8 bg-white rounded-lg border p-5">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">{t('catalogDiscounts.info.title')}</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs text-gray-500">
              <div className="flex gap-2"><Tag className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" /><div><p className="font-medium text-gray-700 mb-0.5">{t('catalogDiscounts.info.flatTitle')}</p>{t('catalogDiscounts.info.flatDesc')}</div></div>
              <div className="flex gap-2"><Layers className="w-4 h-4 text-purple-400 flex-shrink-0 mt-0.5" /><div><p className="font-medium text-gray-700 mb-0.5">{t('catalogDiscounts.info.tieredTitle')}</p>{t('catalogDiscounts.info.tieredDesc')}</div></div>
              <div className="flex gap-2"><Target className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" /><div><p className="font-medium text-gray-700 mb-0.5">{t('catalogDiscounts.info.targetedTitle')}</p>{t('catalogDiscounts.info.targetedDesc')}</div></div>
            </div>
            <div className="mt-3 p-3 bg-amber-50 rounded-lg"><p className="text-xs text-amber-700"><strong>{t('catalogDiscounts.modal.stackable')}:</strong> {t('catalogDiscounts.info.stackingNote')}</p></div>
          </div>
        </main>
      </div>
      <Footer />
      {showModal && <DiscountModal discount={editDiscount} onClose={() => { setShowModal(false); setEditDiscount(null); }} onSave={handleSave} brands={brands} categories={categories} t={t} />}

      <FloatingActionBar count={selectedIds.size} onClear={clearSelection} onSelectAll={selectAll}>
        <button
          onClick={handleBulkToggle}
          disabled={bulkActionLoading}
          className="px-3 py-1.5 bg-amber-100 text-amber-800 rounded-lg text-sm font-medium hover:bg-amber-200 disabled:opacity-50"
        >
          <ToggleRight className="w-4 h-4 inline mr-1" />
          {t('catalogDiscounts.toggleActive', 'Toggle Active')}
        </button>
        <button
          onClick={handleBulkDelete}
          disabled={bulkActionLoading}
          className="px-3 py-1.5 bg-red-100 text-red-800 rounded-lg text-sm font-medium hover:bg-red-200 disabled:opacity-50"
        >
          <Trash2 className="w-4 h-4 inline mr-1" />
          {t('catalogDiscounts.deleteSelected', 'Delete Selected')}
        </button>
      </FloatingActionBar>

      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        icon="danger"
        confirmLabel={t('catalogDiscounts.delete', 'Delete')}
        confirmColor="red"
        onConfirm={confirmModal.onConfirm}
        onCancel={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
      />
    </div>
  );
};

export default CatalogDiscounts;